/**
 * Raw Products Table Component
 * @see sprint-datagrid-migration Phase 1.6
 */

import { useMemo, useCallback } from 'react';
import { Package, AlertTriangle, Plus } from 'lucide-react';
import {
  DataGrid,
  Badge,
  EmptyState,
  textColumn,
  moneyColumn,
  type DataGridColumn,
  type DataGridSortState,
} from '@bill/ui';
import {
  MarketType,
  MarketTypeLabels,
  type RawProductWithRelations,
  type PaginationInfo,
} from '@bstock/shared';
import { formatCentimes } from '@/lib/format';
import { useCurrency } from '@/hooks';

const MARKET_BADGE_VARIANTS: Record<MarketType, 'food' | 'nonfood' | 'maintenance'> = {
  [MarketType.FOOD]: 'food',
  [MarketType.FOOD_IND]: 'food',
  [MarketType.NON_FOOD]: 'nonfood',
  [MarketType.NON_FOOD_IND]: 'nonfood',
  [MarketType.MAINTENANCE]: 'maintenance',
};

export type SortField = 'name' | 'sku' | 'createdAt' | 'updatedAt';
export type SortOrder = 'asc' | 'desc';

const SORT_FIELD_MAP: Record<string, SortField> = {
  name: 'name',
  sku: 'sku',
};

interface RawProductsTableProps {
  products: RawProductWithRelations[];
  pagination: PaginationInfo;
  isLoading: boolean;
  error?: Error | null;
  onRowClick: (productId: string) => void;
  onPageChange: (page: number) => void;
  onLimitChange?: (limit: number) => void;
  sortBy?: SortField;
  sortOrder?: SortOrder;
  onSort?: (field: SortField, order: SortOrder) => void;
  hasFilters?: boolean;
  onCreateClick?: () => void;
  onFetchAllRows?: () => Promise<RawProductWithRelations[]>;
  showFinancials?: boolean;
}

export function RawProductsTable({
  products,
  pagination,
  isLoading,
  error,
  onRowClick,
  onPageChange,
  onLimitChange,
  sortBy = 'name',
  sortOrder = 'asc',
  onSort,
  hasFilters,
  onCreateClick,
  onFetchAllRows,
  showFinancials = false,
}: RawProductsTableProps) {
  const currency = useCurrency();

  const columns = useMemo<DataGridColumn<RawProductWithRelations>[]>(() => {
    const cols: DataGridColumn<RawProductWithRelations>[] = [
      textColumn<RawProductWithRelations>({
        id: 'name',
        header: 'Nom',
        accessor: 'name',
        ellipsis: true,
        maxWidth: '300px',
        cell: (r) => (
          <div className="flex items-center gap-3">
            <div
              className="p-2 rounded-lg flex-shrink-0"
              style={{
                backgroundColor: r.category.color
                  ? `${r.category.color}20`
                  : 'rgb(var(--color-primary-100))',
              }}
            >
              <Package
                className="w-5 h-5"
                style={{
                  color: r.category.color || 'rgb(var(--color-primary-500))',
                }}
              />
            </div>
            <div className="min-w-0">
              <p className="font-medium text-dark-500 truncate">{r.name}</p>
              {r.allergens.length > 0 && (
                <p className="text-xs text-warning-600 mt-0.5 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                  {r.allergens.join(', ')}
                </p>
              )}
            </div>
          </div>
        ),
      }),
      textColumn<RawProductWithRelations>({
        id: 'category',
        header: 'Catégorie',
        sortable: false,
        accessor: (r) => r.category.name,
      }),
      textColumn<RawProductWithRelations>({
        id: 'marketType',
        header: 'Marché',
        sortable: false,
        accessor: (r) => MarketTypeLabels[r.marketType],
        cell: (r) => (
          <Badge variant={MARKET_BADGE_VARIANTS[r.marketType]}>
            {MarketTypeLabels[r.marketType]}
          </Badge>
        ),
      }),
      textColumn<RawProductWithRelations>({
        id: 'sku',
        header: 'SKU',
        accessor: 'sku',
        cellClassName: 'font-mono text-xs',
      }),
      textColumn<RawProductWithRelations>({
        id: 'recipeUnit',
        header: 'Unité recette',
        sortable: false,
        accessor: (r) => r.recipeUnit.symbol,
      }),
    ];

    if (showFinancials) {
      cols.push(
        moneyColumn<RawProductWithRelations>({
          id: 'currentPmp',
          header: 'PMP',
          sortable: false,
          accessor: (r) => (r.currentPmp != null ? r.currentPmp / 100 : null),
          cell: (r) => {
            if (r.currentPmp == null) return <span className="text-neutral-400">—</span>;
            return (
              <span className="text-dark-500 font-medium">
                {formatCentimes(r.currentPmp, currency)}/{r.currentPmpUnitSymbol ?? 'kg'}
              </span>
            );
          },
        })
      );
    }

    cols.push(
      textColumn<RawProductWithRelations>({
        id: 'isActive',
        header: 'Statut',
        sortable: false,
        align: 'center',
        exportValue: (r) => (r.isActive ? 'Actif' : 'Inactif'),
        cell: (r) =>
          r.isActive ? (
            <Badge variant="success">Actif</Badge>
          ) : (
            <Badge variant="neutral">Inactif</Badge>
          ),
      })
    );

    return cols;
  }, [showFinancials, currency]);

  // ---- Sort (server-side, controlled) ------------------------------------
  const sortState = useMemo<DataGridSortState | null>(() => {
    if (!sortBy || !sortOrder) return null;
    const columnId = Object.entries(SORT_FIELD_MAP).find(
      ([, v]) => v === sortBy
    )?.[0];
    if (!columnId) return null;
    return { columnId, direction: sortOrder };
  }, [sortBy, sortOrder]);

  const handleSortChange = useCallback(
    (next: DataGridSortState | null) => {
      if (!onSort) return;
      if (!next) {
        onSort('name', 'asc');
        return;
      }
      const backendField = SORT_FIELD_MAP[next.columnId];
      if (backendField) onSort(backendField, next.direction);
    },
    [onSort]
  );

  if (error) {
    return (
      <div className="bg-white border border-neutral-200 rounded-xl p-8 text-center shadow-sm">
        <AlertTriangle className="w-8 h-8 text-danger-500 mx-auto mb-2" />
        <p className="text-danger-500">Erreur lors du chargement des produits</p>
        <p className="text-sm text-neutral-500 mt-1">{error.message}</p>
      </div>
    );
  }

  if (!isLoading && products.length === 0) {
    return hasFilters ? (
      <EmptyState
        icon={Package}
        title="Aucun produit trouvé"
        description="Aucun produit ne correspond à vos critères de recherche. Essayez de modifier vos filtres."
      />
    ) : (
      <EmptyState
        icon={Package}
        title="Votre catalogue est vide"
        description="Commencez par créer votre premier produit brut pour alimenter votre catalogue."
        action={
          onCreateClick
            ? { label: 'Créer mon premier produit', onClick: onCreateClick, icon: Plus }
            : undefined
        }
      />
    );
  }

  return (
    <DataGrid<RawProductWithRelations>
      data={products}
      columns={columns}
      rowKey={(r) => r.id}
      isLoading={isLoading}
      skeletonRows={10}
      onRowClick={(r) => onRowClick(r.id)}
      currency={currency}
      sortState={sortState}
      onSortChange={handleSortChange}
      exportable={{
        fileName: { base: 'matieres_premieres' },
        sheetName: 'Matières premières',
        position: 'header',
        onFetchAllRows,
      }}
      pagination={{
        page: pagination.page,
        limit: pagination.limit,
        total: pagination.total,
        totalPages: pagination.totalPages,
        onPageChange,
        onLimitChange,
      }}
      emptyState={
        <div className="py-8 text-center text-sm text-neutral-500">
          Aucune matière première trouvée
        </div>
      }
    />
  );
}
